import React from 'react'
// c:/xampp/htdocs/miners-market-main/components/AddItemWizard.js

import { formatNumberWithCommas, isValidUrl, logToFile, REBUILD_CLASSIFICATIONS } from '../utils/shared.js';
import ProductForm from './ProductForm.jsx';
import ConsumablesForm from './forms/ConsumablesForm.jsx';
import RentalForm from './forms/RentalForm.jsx';
import OreSaleForm from './forms/OreSaleForm.jsx';
import MineSaleForm from './forms/MineSaleForm.jsx';
import RefurbishingForm from './forms/RefurbishingForm.jsx';
import RebuildMatrixForm from './RebuildMatrixForm.jsx';
import MediaManager from './forms/MediaManager.jsx';
import AccordionSection from './AccordionSection.jsx';

// Define available listing types for the first step of the wizard
const LISTING_TYPES = [
    // ... existing code ...
    { id: 'standard', label: 'Standard Sale', icon: 'fas fa-tag', desc: 'Sell whole equipment or machinery.' },
    { id: 'components', label: 'Components & Spares', icon: 'fas fa-cogs', desc: 'Sell individual components and spare parts.' },
    { id: 'rebuild', label: 'Equipment Rebuild', icon: 'fas fa-tools', desc: 'List a machine for rebuild projects.' },
    { id: 'rental', label: 'Rental', icon: 'fas fa-clock', desc: 'List equipment for hire.' },
    { id: 'consumable', label: 'Consumables', icon: 'fas fa-box-open', desc: 'Sell consumable supplies.' },
    { id: 'commercial', label: 'Commercial / Services', icon: 'fas fa-briefcase', desc: 'Mines, Ore, or Services.' },
    { id: 'miscellaneous', label: 'Miscellaneous', icon: 'fas fa-layer-group', desc: 'Bearings, Filters, Conveyor Belts, Greases, Diesel.' },
];

// NEW: Miscellaneous Sub-categories
const MISCELLANEOUS_SUBCATEGORIES = [
    { id: 'bearings', label: 'Bearings', icon: 'fas fa-cog', desc: 'Industrial bearings for mining equipment.' },
    { id: 'filters', label: 'Filters', icon: 'fas fa-filter', desc: 'Oil, fuel, air, and hydraulic filters.' },
    { id: 'conveyor_belts', label: 'Conveyor Belts', icon: 'fas fa-conveyor-belt', desc: 'Industrial conveyor belt systems.' },
    { id: 'greases', label: 'Greases', icon: 'fas fa-oil-can', desc: 'Industrial lubricants and greases.' },
    { id: 'diesel', label: 'Diesel', icon: 'fas fa-gas-pump', desc: '50ppm diesel fuel and lubricants.' },
];

export default function AddItemWizard({ isOpen, onClose, categories, user, onListingAdded, draft = null }) {
    const [step, setStep] = React.useState(1);
    const [selectedListingType, setSelectedListingType] = React.useState(null);
    const [selectedCategory, setSelectedCategory] = React.useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = React.useState(null);
    const [selectedMiscSubCategory, setSelectedMiscSubCategory] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [saveStatus, setSaveStatus] = React.useState('idle'); // idle, saving, saved, error
    const debounceTimeout = React.useRef(null);
    const [uploadingImages, setUploadingImages] = React.useState({}); // NEW: State for image loading spinners
    const [activeCompressorTab, setActiveCompressorTab] = React.useState(null); // NEW: For compressor fleet management tabs

    const [formError, setFormError] = React.useState(null);
    const [formSuccess, setFormSuccess] = React.useState(null);
    const [openSection, setOpenSection] = React.useState('productInfo');
    // create function
    
    const initialFormData = React.useMemo(() => ({
        // This object will be populated by the specific forms
        // We pre-fill contact details from the user profile
        sales_contact_name: user.name || '',
        sales_contact_phone: user.phone_number || '',
        sales_contact_email: user.email || '',
        company_name_address: user.address || '',
        rebuild_classification: null, // NEW
        rebuild_details: {}, // NEW: For the rebuild matrix
    }), [user]);

    const [formData, setFormData] = React.useState(draft || initialFormData);

    // --- NEW: Ref to hold latest form data for event listeners ---
    const formDataRef = React.useRef(formData);
    React.useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    // Reset state when the modal is closed or opened
    React.useEffect(() => {
        if (isOpen) {
            // Reset common state
            setFormError(null);
            setFormSuccess(null);
            setSaveStatus('idle');

            if (draft) {
                // --- Resuming a draft ---
                // Fetch full details because the draft prop from the dashboard list is incomplete
                const fetchDraftDetails = async () => {
                    try {
                        const response = await fetch(`/api/get_listing_details.php?id=${draft.id}&type=${draft.listingType}`, { credentials: 'include' });
                        if (!response.ok) throw new Error('Failed to load draft details');
                        const fullDraft = await response.json();

                        // Normalize form_type
                        fullDraft.form_type = fullDraft.listingType;

                         // --- Normalize image and doc URLs (important for edits/drafts) ---
                         for (let i = 1; i <= 10; i++) {
                            const imageKey = `image_url_${i}`;
                            if (fullDraft[imageKey]) {
                                // Images get put directly into formData, keyed by URL
                                // We do NOT want to load file objects here
                        if (!isValidUrl(fullDraft[imageKey])) console.warn(`[AddItemWizard] Invalid image URL detected during draft load: ${fullDraft[imageKey]}`);
                                console.log(`[AddItemWizard] Loaded draft image ${i}:`, fullDraft[imageKey]);
                            }
                         }
    
                        const docFields = ['doc_manual_url', 'doc_datasheet_url', 'doc_conformity_url', 'doc_calibration_url', 'doc_warranty_url', 'doc_service_history_url', 'doc_factory_test_url', 'doc_oil_report_url'];
                         docFields.forEach(field => {
                             if (fullDraft[field]) {
                                 console.log(`[AddItemWizard] Loaded draft doc:`, field, fullDraft[field]);
                                 if (!isValidUrl(fullDraft[field])) console.warn(`[AddItemWizard] Invalid doc URL detected during draft load: ${fullDraft[field]}`);
                             }
                         });
                         //--- NEW: Handle dynamic datapack uploads (1-25) ---
                         for (let i = 1; i <= 25; i++) {
                            const docKey = `doc_datasheet_${i}_url`;
                            // --- FIX: Only validate and log if the key exists and has a value ---
                            if (fullDraft[docKey]) {
                                if (!isValidUrl(fullDraft[docKey])) {
                                    console.warn(`[AddItemWizard] Invalid datasheet URL detected for ${docKey}:`, fullDraft[docKey]);
                                }
                                console.log(`[AddItemWizard] Loaded draft datapack ${i}:`, fullDraft[docKey]);
                            }
                         }

                        // Parse rebuild_details if it's a JSON string
                        if (fullDraft.rebuild_details && typeof fullDraft.rebuild_details === 'string') {
                            try {
                                fullDraft.rebuild_details = JSON.parse(fullDraft.rebuild_details);
                            } catch (e) {
                                console.error("Error parsing draft rebuild_details:", e);
                                fullDraft.rebuild_details = {};
                            }
                        } else if (!fullDraft.rebuild_details) {
                             fullDraft.rebuild_details = {};
                        }

                        // Ensure rebuild_details is a valid object to prevent crashes
                        if (!fullDraft.rebuild_details || typeof fullDraft.rebuild_details !== 'object') {
                            console.warn("[AddItemWizard] Invalid rebuild_details structure, resetting to empty object");
                            fullDraft.rebuild_details = {};
                        }

                        // Migrate legacy rebuild_details to current structure
                        // This ensures old drafts don't cause errors with new form structure
                        if (fullDraft.rebuild_details && typeof fullDraft.rebuild_details === 'object') {
                            // The RebuildMatrixForm will handle the migration when rendering
                            // Just ensure it's a valid object
                            if (Object.keys(fullDraft.rebuild_details).length > 0) {
                                console.log("[AddItemWizard] Loaded rebuild_details with", Object.keys(fullDraft.rebuild_details).length, "items");
                            }
                        }

                        setFormData(fullDraft);
                        setSelectedListingType(fullDraft.form_type);

                        const category = categories.find(c => c.id === fullDraft.category_id);
                        if (category) {
                            if (category.parent_id) {
                                setSelectedSubCategory(category);
                                setSelectedCategory(categories.find(c => c.id === category.parent_id));
                            } else {
                                setSelectedCategory(category);
                                setSelectedSubCategory(null);
                            }
                        }
                        
                        setStep(5); 
                        setOpenSection('productInfo');
                    } catch (error) {
                        console.error("Error fetching draft details:", error);
                        setFormError("Failed to load draft. Please try again.");
                    }
                };

                fetchDraftDetails();

            } else {
                // --- Starting a new listing ---
                setStep(1);
                setSelectedListingType(null);
                setSelectedCategory(null);
                setSelectedSubCategory(null);
                setFormData(initialFormData);
                setOpenSection('productInfo');
            }
        }
    }, [isOpen, draft, initialFormData, categories]);

    // --- Centralized file upload handler ---
    const handleFileUpload = async (files, fieldName) => {
        if (files.length === 0) return;

        await logToFile(`[handleFileUpload] Starting for field: ${fieldName}, Files: ${files.length}`);

        const isImageUpload = fieldName === 'image_files';
        setSaveStatus('saving'); // Use this as a generic "processing" indicator
        setFormError(null);

        // --- NEW: Client-side file size validation (100MB max) ---
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        const oversizedFiles = files.filter(f => f.size > maxSize);
        if (oversizedFiles.length > 0) {
            const errorMessage = `File(s) exceed 100MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`;
            await logToFile(`[handleFileUpload] ${errorMessage}`);
            setFormError(errorMessage);
            setSaveStatus('error');
            return;
        }

        const uploadFormData = new FormData();
        let hasFilesToUpload = false;

        if (fieldName === 'image_files' || fieldName === 'doc_datasheet_files') {
            // Handle multi-file uploads
            const prefix = fieldName === 'image_files' ? 'image' : 'doc_datasheet';
            const maxSlots = prefix === 'image' ? 10 : 25;
            
            const newUploadingState = {};
            let currentFileIndex = 1;
            for (const file of files) {
                while (currentFileIndex <= maxSlots && formData[`${prefix}_url_${currentFileIndex}`]) {
                    currentFileIndex++;
                }
                if (currentFileIndex > maxSlots) {
                    alert(`Maximum number of ${prefix}s reached.`);
                    break;
                }
                uploadFormData.append(`${prefix}_${currentFileIndex}`, file);
                hasFilesToUpload = true;
                if (isImageUpload) {
                    // Mark this slot as uploading
                    newUploadingState[currentFileIndex] = true;
                }
                currentFileIndex++;
            }
        } else {
            // Handle single file uploads
            uploadFormData.append(fieldName.replace('_file', ''), files[0]);
            hasFilesToUpload = true;
        }

        if (!hasFilesToUpload) {
            setSaveStatus('idle');
            if (isImageUpload) {
                // Clear any uploading state if no files were actually added
                setUploadingImages({});
            }
            await logToFile(`[handleFileUpload] No files to upload.`);
            return;
        }

        try {
            await logToFile(`[handleFileUpload] Sending fetch request to upload_files.php`);
            const response = await fetch('/api/upload_files.php', { method: 'POST', credentials: 'include', body: uploadFormData });
            
            const responseText = await response.text();
            await logToFile(`[handleFileUpload] Received response text: ${responseText}`);

            const result = JSON.parse(responseText);
            await logToFile(`[handleFileUpload] Parsed response JSON: ${JSON.stringify(result)}`);

            if (!result.success) {
                const errorMessage = result.message || (result.errors ? result.errors.join(', ') : 'File upload failed.');
                throw new Error(errorMessage);
            }

            // Update form state with the new URLs
            await logToFile(`[handleFileUpload] Upload success. Updating form state with URLs: ${JSON.stringify(result.urls)}`);
            setFormData(prev => ({ ...prev, ...result.urls }));
            setSaveStatus('saved'); // Trigger auto-save with new URLs
        } catch (err) {
            await logToFile(`[handleFileUpload] CATCH block error: ${err.message}`);
            setFormError(`Upload failed: ${err.message}`);
            setSaveStatus('error');
        } finally {
            // --- NEW: Clear uploading state after completion ---
            if (isImageUpload) {
                setUploadingImages({});
            }
        }
    };

    // --- NEW: Auto-save logic ---
    React.useEffect(() => {
        // Don't auto-save on initial load, if we are on selection steps, or if no form type is set
        // OR if the listing is currently being submitted or is already published
        if (!isOpen || step < 5 || !formData.form_type || isSubmitting || formData.status === 'pending') {
            return;
        }

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        setSaveStatus('saving');

        debounceTimeout.current = setTimeout(() => {
            handleSaveDraft(false); // false = don't show success message or close
        }, 1000); // Reduced to 1-second debounce for faster saves

        return () => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        };
    }, [formData, isOpen, step]);

    // --- NEW: Robust Save on Unload/Hide logic ---
    React.useEffect(() => {
        const handleSaveOnExit = () => {
            const currentData = formDataRef.current;
            // Only save if we are in the form, not on the selection steps, and have a form type.
            // AND only if it's still a draft (not published/pending)
            if (isOpen && step >= 5 && currentData.form_type && currentData.status !== 'pending') {
                // If there's a pending debounced save, clear it to prevent a double-send.
                if (debounceTimeout.current) {
                    clearTimeout(debounceTimeout.current);
                }
                console.log('[AddItemWizard] Page hidden/closed. Saving draft via beacon...');
                const payload = JSON.stringify({ ...currentData, seller_id: user.id });
                const blob = new Blob([payload], { type: 'application/json' });
                try {
                    const success = navigator.sendBeacon('/api/save_draft.php', blob);
                    console.log(`[AddItemWizard] sendBeacon call queued successfully: ${success}`);
                } catch (e) {
                    console.error('[AddItemWizard] Error calling sendBeacon:', e);
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                handleSaveOnExit();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handleSaveOnExit); // Add pagehide for better browser close support

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handleSaveOnExit);
        };
    }, [isOpen, step, user.id]); // Removed formData from dependencies to prevent listener thrashing

    // --- NEW: Handle Listing Type Selection (Step 1) ---
    const handleListingTypeSelect = (typeId) => {
        setSelectedListingType(typeId);

        let formType = typeId;
        let market; // Let it be undefined unless specified

        if (typeId === 'components') {
            formType = 'standard'; // Use the standard product form
            market = 'components';
        } else if (typeId === 'standard') {
            market = 'standard';
        } else if (typeId === 'miscellaneous') {
            market = 'miscellaneous';
        }

        // For rebuild, form_type will be 'rebuild'. For others, it's the same as typeId.
        // The market will be set for standard/components/miscellaneous, and undefined for others.
        setFormData(prev => ({ ...prev, form_type: formType, listing_market: market }));

        if (typeId === 'rebuild') {
            setStep(2); // Go to new classification step
        } else if (typeId === 'miscellaneous') {
            setStep(2); // Go to miscellaneous sub-category selection step
        } else {
            setStep(3); // Go to original step 2 (category selection)
        }
    };

    const handleMiscSubCategorySelect = (subCategoryId) => {
        setSelectedMiscSubCategory(subCategoryId);

        // Map sub-category to form type
        const formTypeMap = {
            'bearings': 'standard',
            'filters': 'filter',
            'conveyor_belts': 'conveyor',
            'greases': 'grease',
            'diesel': 'diesel'
        };

        setFormData(prev => ({ ...prev, form_type: formTypeMap[subCategoryId] }));
        setStep(5); // Go to form
        setOpenSection('productInfo');
    };

    // NEW handler for selecting a rebuild classification
    const handleRebuildClassificationSelect = (classificationId) => {
        setFormData(prev => ({ ...prev, rebuild_classification: classificationId }));
        setStep(3); // Move to category selection
    };

    // --- NEW: Filter Categories based on Listing Type (Step 2) ---
    const filteredMainCategories = React.useMemo(() => {
        if (!selectedListingType || !categories) return [];
        const allMain = categories.filter(c => !c.parent_id);

        if (selectedListingType === 'rebuild') {
            return allMain.filter(c => ['surface-equipment', 'underground-equipment', 'plant'].includes(c.slug));
        }
        if (selectedListingType === 'standard' || selectedListingType === 'components') {
            const excluded = ['rental-equipment', 'consumables', 'ore-sales', 'mines', 'refurbishing-services'];
            return allMain.filter(c => !excluded.includes(c.slug));
        }
        if (selectedListingType === 'rental') return allMain.filter(c => c.slug === 'rental-equipment');
        if (selectedListingType === 'consumable') return allMain.filter(c => c.slug === 'consumables');
        if (selectedListingType === 'commercial') return allMain.filter(c => ['ore-sales', 'mines', 'refurbishing-services'].includes(c.slug));

        return [];
    }, [categories, selectedListingType]);

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setActiveCompressorTab(null); // Reset compressor tab when category changes
        // If the category has no sub-categories, skip to the form step
        const hasSubCategories = categories.some(sub => sub.parent_id === category.id);
        if (hasSubCategories) {
            setStep(4);
        } else {
            const type = selectedListingType === 'rebuild' ? 'rebuild' : category.form_type;
            setFormData(prev => ({ ...prev, category_id: category.id, form_type: type }));
            setStep(5);
        }
    };

    const handleSubCategorySelect = (subCategory) => {
        setSelectedSubCategory(subCategory);
        const type = selectedListingType === 'rebuild' ? 'rebuild' : selectedCategory.form_type;
        setFormData(prev => ({ ...prev, category_id: subCategory.id, form_type: type }));
        setStep(5);
    };

    const handleFormChange = async (e) => {
        const { name, value, type, files, checked } = e.target;

        // --- NEW DEBUGGING ---   
        const formChangeEvent = `[AddItemWizard] handleFormChange triggered. Name: ${name}, Type: ${type}`;
        console.log(formChangeEvent);
        await logToFile(formChangeEvent);
        if (type === 'file') {
            const fileChangeEvent = `[AddItemWizard] File input change detected. Name: ${name}, files: ${files.length}`;
            console.log(fileChangeEvent);
            await logToFile(fileChangeEvent);
        }
        // --- END DEBUGGING ---

        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (type === 'file' && files && files.length > 0) {
            // NEW: Delegate all file handling to the uploader
            console.log('[AddItemWizard] Processing file upload...');
            await logToFile('[AddItemWizard] Processing file upload...');
            await handleFileUpload(Array.from(files), name);
            await logToFile('after handleFileUpload');
            e.target.value = ''; // Reset file input to allow re-selecting the same file
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRemoveDataPackFile = (indexToRemove) => {
        setFormData(prev => ({ ...prev, [`doc_datasheet_${indexToRemove}_url`]: null }));
    };

    const handleRemoveImage = (indexToRemove) => {
        setFormData(prev => ({ ...prev, [`image_url_${indexToRemove}`]: null }));
    };

    const handleSaveDraft = async (closeOnSave = false) => {
        // Don't save if there's no listing type selected
        if (!formData.form_type || formData.status === 'pending' || formData.status === 'approved') return;

        setSaveStatus('saving');
        console.log('[AddItemWizard] Attempting to save draft via fetch...', { draftId: formData.id, formType: formData.form_type });

        const payload = { ...formData, seller_id: user.id };

        // Strip commas before saving to DB
        const formattedFields = ['price', 'base_price', 'rate_per_hour', 'quantity', 'hours', 'year', 'quantity_available'];
        formattedFields.forEach(field => {
            if (payload[field]) payload[field] = payload[field].toString().replace(/,/g, '');
        });

        if (payload.rebuild_details) {
            const cleanDetails = typeof payload.rebuild_details === 'string' ? JSON.parse(payload.rebuild_details) : { ...payload.rebuild_details };
            Object.keys(cleanDetails).forEach(key => {
                if (cleanDetails[key]?.hours) {
                    cleanDetails[key].hours = cleanDetails[key].hours.toString().replace(/,/g, '');
                }
            });
            payload.rebuild_details = JSON.stringify(cleanDetails);
        }

        // Clean up any lingering file objects just in case (shouldn't be any with new flow)
        Object.keys(payload).forEach(key => { if (key.includes('_file')) delete payload[key]; });

        try {
            const response = await fetch('/api/save_draft.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to save draft.');

            console.log('[AddItemWizard] Draft saved successfully via fetch.', result);
            // Update formData with the draft_id from the backend
            if (result.draft_id) {
                setFormData(prev => ({ ...prev, id: result.draft_id, listingType: result.listingType }));
            }
            setSaveStatus('saved');


            if (closeOnSave) {
                onListingAdded(); // This will refresh the dashboard
                onClose();
            }
        } catch (err) {
            console.error('[AddItemWizard] Error saving draft via fetch:', err);
            setSaveStatus('error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // --- FIX: Immediately clear any pending auto-save timeout and reset status ---
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
            debounceTimeout.current = null;
            setSaveStatus('idle'); // Reset status to prevent "Auto-saving..." from lingering
        }
        
        // --- NEW: Intercept for Rebuild Flow Steps ---
        if (formData.form_type === 'rebuild') {
            if (step === 5) { // After Core Info
                setOpenSection('PowertrainDrivetrain'); // Auto-open first matrix section
                setStep(6);
                return;
            }
            if (step === 6) { // After Matrix
                setOpenSection('basePrice'); // Auto-open pricing section
                setStep(7);
                return;
            }
            if (step === 7) { // After Pricing
                setStep(8);
                return;
            }
        }


        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        try {
            // With instant uploads, file handling is already done. We just prepare the payload.
            const payload = {
                ...formData,
                status: 'pending', // Explicitly set status to pending for final submission
                seller_id: user.id,
            };

            // --- NEW: Strip commas before final submission ---
            const formattedFields = ['price', 'base_price', 'rate_per_hour', 'quantity', 'hours', 'year', 'quantity_available'];
            formattedFields.forEach(field => {
                if (payload[field]) payload[field] = payload[field].toString().replace(/,/g, '');
            });

            if (payload.rebuild_details) {
                const cleanDetails = typeof payload.rebuild_details === 'string' ? JSON.parse(payload.rebuild_details) : { ...payload.rebuild_details };
                Object.keys(cleanDetails).forEach(key => {
                    if (cleanDetails[key]?.hours) {
                        cleanDetails[key].hours = cleanDetails[key].hours.toString().replace(/,/g, '');
                    }
                });
                payload.rebuild_details = JSON.stringify(cleanDetails);
            }

            // Clean up file objects from payload
            Object.keys(payload).forEach(key => {
                if (key.includes('_file')) delete payload[key];
            });

            // Submit to the endpoint
            const endpoint = '/api/add_listing.php';
            
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'include', // Include user session cookie for authentication
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Failed to create listing.');

            // --- FIX: Immediately update state AND ref to prevent race conditions ---
            const publishedData = {
                ...formData,
                status: 'pending',
                listing_number: result.listing_number,
                id: result.id || formData.id
            };
            
            setFormData(publishedData);
            formDataRef.current = publishedData; // Synchronous update for the beacon listener

            setFormSuccess(result.message || 'Listing submitted for approval!');
            
            // After successful submission, refresh the listing and close the wizard
            onListingAdded(); // Callback to refresh the dashboard list
            
            // Shorten duration to 1 second: enough for feedback, fast enough to prevent exit glitches
            onClose(); // Close the modal instantly as requested


        } catch (err) {
            setFormError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderForm = () => {
        const formProps = { formData, onFormChange: handleFormChange, categories, openSection, onToggleSection: (id) => setOpenSection(id), uploadingImages };
        switch (formData.form_type) {
            case 'standard': return <ProductForm {...formProps} />;
            case 'rental': return <RentalForm {...formProps} />;
            case 'consumable': return <ConsumablesForm {...formProps} />;
            case 'ore': return <OreSaleForm {...formProps} />;
            case 'mine': return <MineSaleForm {...formProps} />;
            case 'service': return <RefurbishingForm {...formProps} />;
            case 'diesel': return <ProductForm {...formProps} />;
            case 'filter': return <ProductForm {...formProps} />;
            case 'conveyor': return <ProductForm {...formProps} />;
            case 'grease': return <ProductForm {...formProps} />;
            case 'rebuild':
                if (step === 5) return <ProductForm {...formProps} />;
                if (step === 6) {
                    try {
                        return <RebuildMatrixForm {...formProps} />;
                    } catch (e) {
                        console.error("Error rendering RebuildMatrixForm:", e);
                        return (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 font-medium">Error loading rebuild matrix</p>
                                <p className="text-red-600 text-sm mt-1">Your draft data may be incompatible with the current form structure. Your data has been preserved. Please contact support if this issue persists.</p>
                                <button
                                    onClick={() => setStep(5)}
                                    className="mt-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                                >
                                    Go Back
                                </button>
                            </div>
                        );
                    }
                }
                if (step === 7) return <RebuildBasePriceForm {...formProps} />;
                if (step === 8) return <RebuildReview {...formProps} />;
                return <p className="text-red-500">Error: Unknown step.</p>;
            default: return <p className="text-red-500">Error: No form available for this category.</p>;
        }
    };

    const subCategories = selectedCategory ? categories.filter(c => c.parent_id === selectedCategory.id) : [];

    if (!isOpen) return null;

    const isRebuildFlow = selectedListingType === 'rebuild';
    const getStepTitle = () => {
        if (isRebuildFlow) {
            switch(step) {
                case 1: return "Step 1: Select Listing Type";
                case 2: return "Step 2: Select Rebuild Classification";
                case 3: return "Step 3: Select Equipment Category";
                case 4: return "Step 4: Select Equipment Sub-Category";
                case 5: return "Step 5: Core Equipment Information";
                case 6: return "Step 6: Equipment Rebuild Declaration";
                case 7: return "Step 7: Base Machine Pricing";
                case 8: return "Step 8: Review & Publish";
                default: return "Add New Item";
            }
        } else {
             switch(step) {
                case 1: return "Step 1: Select Listing Type";
                case 3: return "Step 2: Select Category";
                case 4: return "Step 3: Select Sub-Category";
                case 5: return "Step 4: Item Information & Submission";
                default: return "Add New Item";
            }
        }
    };

    const handleBack = () => {
        if (step === 3 && !isRebuildFlow) {
            setStep(1);
        } else {
            setStep(step - 1);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] flex flex-col">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {getStepTitle()}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-2xl" aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-grow">
                    {step === 1 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {LISTING_TYPES.map(type => (
                                <button key={type.id} onClick={() => handleListingTypeSelect(type.id)} className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                                    <i className={`${type.icon} text-3xl text-blue-600 mb-3`}></i>
                                    <span className="font-bold text-gray-800 block mb-1">{type.label}</span>
                                    <span className="text-xs text-gray-500">{type.desc}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 2 && isRebuildFlow && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {REBUILD_CLASSIFICATIONS.map(cat => (
                                <button key={cat.id} onClick={() => handleRebuildClassificationSelect(cat.id)} className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                                    <i className={`${cat.icon} text-3xl mb-3`}></i>
                                    <span className="font-bold text-gray-800 block mb-1">{cat.title}</span>
                                    <span className="text-xs text-gray-500">{cat.desc}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 2 && selectedListingType === 'miscellaneous' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {MISCELLANEOUS_SUBCATEGORIES.map(cat => (
                                <button key={cat.id} onClick={() => handleMiscSubCategorySelect(cat.id)} className="p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:shadow-md transition-all text-center flex flex-col items-center h-full">
                                    <i className={`${cat.icon} text-3xl text-blue-600 mb-3`}></i>
                                    <span className="font-bold text-gray-800 block mb-1">{cat.label}</span>
                                    <span className="text-xs text-gray-500">{cat.desc}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 3 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {filteredMainCategories.map(cat => (
                                <button key={cat.id} onClick={() => handleCategorySelect(cat)} className="p-4 border rounded-lg hover:bg-gray-100 hover:shadow-md transition-all text-center">
                                    <span className="font-semibold">{cat.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {step === 4 && (
                        <>
                        {selectedCategory && selectedCategory.slug === 'compressor-fleet-management' ? (
                            // --- NEW: Tabbed interface for Compressor Fleet Management ---
                            <div>
                                <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200">
                                    {subCategories.map(sub => (
                                        <button
                                            key={sub.id}
                                            onClick={() => setActiveCompressorTab(sub.id)}
                                            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                                                activeCompressorTab === sub.id
                                                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                                                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                                            }`}
                                        >
                                            {sub.name}
                                        </button>
                                    ))}
                                </div>
                                <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                                    {activeCompressorTab ? (
                                        <div className="text-center">
                                            <p className="text-gray-900 font-semibold mb-2">
                                                {subCategories.find(s => s.id === activeCompressorTab)?.name}
                                            </p>
                                            <p className="text-sm text-gray-600 mb-4">
                                                Select this category to continue with your listing
                                            </p>
                                            <button
                                                onClick={() => {
                                                    const sub = subCategories.find(s => s.id === activeCompressorTab);
                                                    if (sub) handleSubCategorySelect(sub);
                                                }}
                                                className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                Select {subCategories.find(s => s.id === activeCompressorTab)?.name}
                                            </button>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center">Please select a tab above to continue</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Default grid for other categories
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {subCategories.map(sub => (
                                    <button key={sub.id} onClick={() => handleSubCategorySelect(sub)} className="p-4 border rounded-lg hover:bg-gray-100 hover:shadow-md transition-all text-center">
                                        <span className="font-semibold">{sub.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        </>
                    )}
                    {(step >= 5) && (
                        <form onSubmit={handleSubmit}>
                            {renderForm()}
                            {formError && <div className="mt-4 text-red-500 text-sm p-3 bg-red-100 rounded-md">{formError}</div>}
                            {formSuccess && <div className="mt-4 text-green-500 text-sm p-3 bg-green-100 rounded-md">{formSuccess}</div>}                            
                            <div className="pt-5 mt-4 border-t flex justify-between items-center">
                                <button type="button" onClick={handleBack} className="text-sm text-gray-600 hover:underline">Back</button>
                                
                                <div className="text-sm">
                                    {saveStatus === 'saving' && <span className="text-gray-500"><i className="fas fa-spinner fa-spin mr-2"></i>Auto-saving...</span>}
                                    {saveStatus === 'saved' && <span className="text-green-600"><i className="fas fa-check-circle mr-2"></i>Saved</span>}
                                    {saveStatus === 'error' && <span className="text-red-500"><i className="fas fa-exclamation-circle mr-2"></i>Save Error</span>}
                                </div>

                                <div className="flex justify-end">
                                    <button type="button" onClick={() => handleSaveDraft(true)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50">
                                        Save Draft & Close
                                    </button>
                                    <button type="submit" disabled={isSubmitting} className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white primary-gradient disabled:opacity-50">
                                        {isSubmitting ? 'Submitting...' : (formData.form_type === 'rebuild' ? (step === 5 ? 'Next: Rebuild Matrix' : (step === 6 ? 'Next: Base Pricing' : (step === 7 ? 'Next: Review' : 'Confirm & Publish'))) : 'Confirm & Publish')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
